import type { IGetMarketsByEventIdRequestParams } from "~/feature/dashboard/types";
import type { EventMarketsFiltersState } from "~/feature/event/providers/EventMarketsFiltersProvider";
import type { IFilterParams } from "~/types/savedFilters";
import { endsInRequestParams } from "~/utils/filters/endsInFilter";
import { stripAllFilterOptions } from "~/utils/filters/filterAllOptions";

export const filterParamsFromEventMarkets = (
  filters: EventMarketsFiltersState
): IFilterParams => ({
  platforms: filters.platforms,
  minChance: filters.minChance,
  maxChance: filters.maxChance,
  minSpread: filters.minSpread,
  minVolume: filters.minVolume,
  maxVolume: filters.maxVolume,
  minLiquidity: filters.minLiquidity,
  maxLiquidity: filters.maxLiquidity,
  endsIn: filters.endsIn,
  endsInCustomType: filters.endsInCustomType,
  endsInValue1: filters.endsInValue1,
  endsInUnit1: filters.endsInUnit1,
  endsInValue2: filters.endsInValue2,
  endsInUnit2: filters.endsInUnit2,
  createdAt: filters.createdAt,
  apyFilter: filters.apyFilter,
  roiFilter: filters.roiFilter,
  minVolume24h: filters.minVolume24h,
  minPriceChange24h: filters.minPriceChange24h,
  maxPriceChange24h: filters.maxPriceChange24h,
});

export const eventMarketsFromFilterParams = (
  params: IFilterParams
): EventMarketsFiltersState => ({
  platforms: params.platforms,
  minChance: params.minChance,
  maxChance: params.maxChance,
  minSpread: params.minSpread,
  minVolume: params.minVolume,
  maxVolume: params.maxVolume,
  minLiquidity: params.minLiquidity,
  maxLiquidity: params.maxLiquidity,
  endsIn: params.endsIn,
  endsInCustomType: params.endsInCustomType,
  endsInValue1: params.endsInValue1,
  endsInUnit1: params.endsInUnit1,
  endsInValue2: params.endsInValue2,
  endsInUnit2: params.endsInUnit2,
  createdAt: params.createdAt,
  apyFilter: params.apyFilter,
  roiFilter: params.roiFilter,
  minVolume24h: params.minVolume24h,
  minPriceChange24h: params.minPriceChange24h,
  maxPriceChange24h: params.maxPriceChange24h,
});

export const requestParamsFromEventMarketsFilters = (
  rawFilters: EventMarketsFiltersState,
  ungroup?: boolean
): Partial<IGetMarketsByEventIdRequestParams> => {
  const filters = stripAllFilterOptions(rawFilters);
  const out: Partial<IGetMarketsByEventIdRequestParams> = {};

  if (ungroup !== undefined) {
    out.ungroup = ungroup;
  }

  Object.assign(out, endsInRequestParams(filters));

  if (filters.platforms?.length) {
    out.platforms = filters.platforms;
  }

  if (filters.maxChance) {
    out.maxChance = filters.maxChance;
  }

  if (filters.minChance) {
    out.minChance = filters.minChance;
  }

  if (filters.minSpread) {
    out.minSpread = filters.minSpread;
  }

  if (filters.minVolume) {
    out.minVolume = filters.minVolume;
  }

  if (filters.maxVolume) {
    out.maxVolume = filters.maxVolume;
  }

  if (filters.minLiquidity) {
    out.minLiquidity = filters.minLiquidity;
  }

  if (filters.maxLiquidity) {
    out.maxLiquidity = filters.maxLiquidity;
  }

  if (filters.createdAt) {
    out.createdAt = filters.createdAt;
  }

  if (filters.apyFilter) {
    out.apyFilter = filters.apyFilter;
  }

  if (filters.roiFilter) {
    out.roiFilter = filters.roiFilter;
  }

  if (filters.minVolume24h != null) {
    out.minVolume24h = filters.minVolume24h;
  }

  if (filters.minPriceChange24h != null) {
    out.minPriceChange24h = filters.minPriceChange24h;
  }

  if (filters.maxPriceChange24h != null) {
    out.maxPriceChange24h = filters.maxPriceChange24h;
  }

  return out;
};

export const hasEventMarketsFilterParams = (
  params: Partial<IGetMarketsByEventIdRequestParams>
): boolean => {
  const { ...rest } = params;

  return Object.keys(rest).length > 0;
};
